/* Fields tab — extracted from FormBuilder.jsx (A7a) */
import React from 'react';
import {
  DndContext,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';

function DroppableRow({ rowId, rowIndex, rowTitle, isEmpty, onRemoveRow, onTitleChange, children }) {
  const { setNodeRef, isOver } = useDroppable({ id: rowId });
  return (
    <div
      ref={setNodeRef}
      className={`fb-drop-row${isOver ? ' is-over' : ''}`}
    >
      <div className="fb-drop-row-header">
        <div className="fb-drop-row-header-main">
          <span className="fb-drop-row-label">Row {rowIndex + 1}</span>
          {isEmpty && (
            <button
              type="button"
              className="fb-btn-ghost-sm"
              onClick={onRemoveRow}
            >
              Remove row
            </button>
          )}
        </div>
        <div className="fb-drop-row-title-block">
          <label className="fb-label" style={{ fontSize: '0.8rem', marginBottom: '0.25rem' }}>
            Section title (optional)
          </label>
          <input
            type="text"
            className="fb-input fb-drop-row-title-input"
            placeholder="e.g. Your details"
            value={rowTitle || ''}
            onChange={(e) => onTitleChange(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
          />
          <span className="fb-faint" style={{ fontSize: '0.75rem', marginTop: '0.2rem' }}>
            Used as the label for Accordion, Tabbed, and Multi-step layouts.
          </span>
        </div>
      </div>
      <div className="fb-drop-row-fields">
        {children}
      </div>
    </div>
  );
}

function DraggableFieldCard({ field, updateField, deleteField }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: field.id, data: { fieldId: field.id } });
  return (
    <div
      ref={setNodeRef}
      className={`fb-field-card${isDragging ? ' is-dragging' : ''}`}
    >
      <div className="fb-field-card-header">
        <span
          role="button"
          tabIndex={0}
          aria-label="Drag to reorder"
          className="fb-drag-handle"
          {...listeners}
          {...attributes}
        >
          ⋮⋮
        </span>
        <strong style={{ flex: 1 }}>{field.label || 'Field'}</strong>
        <button
          type="button"
          className="fb-btn-danger-sm"
          onClick={() => deleteField(field.id)}
        >
          Delete
        </button>
      </div>
      <div className="fb-field-stack">
        <input
          type="text"
          className="fb-input"
          placeholder="Field label"
          value={field.label || ''}
          onChange={(e) => updateField(field.id, { label: e.target.value })}
        />
        <input
          type="text"
          className="fb-input"
          placeholder="Data key (auto-filled from label)"
          value={field.name || ''}
          onChange={(e) => updateField(field.id, { name: e.target.value })}
        />
        <span className="fb-faint" style={{ marginTop: '-0.25rem' }}>
          Data key (auto-filled from the label, edit only if needed)
        </span>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--ink)' }}>
          <input
            type="checkbox"
            checked={field.required || false}
            onChange={(e) => updateField(field.id, { required: e.target.checked })}
          />
          Required
        </label>
        {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
          <div className="fb-options-block">
            <label className="fb-label" style={{ fontSize: '0.85rem' }}>
              {field.type === 'select' ? 'Dropdown options' : field.type === 'radio' ? 'Radio options' : 'Checkbox options'}
            </label>
            {(field.options || []).map((opt, optIndex) => (
              <div key={optIndex} className="fb-option-row">
                <input
                  type="text"
                  className="fb-input"
                  value={opt}
                  onChange={(e) => {
                    const newOpts = [...(field.options || [])];
                    newOpts[optIndex] = e.target.value;
                    updateField(field.id, { options: newOpts });
                  }}
                  placeholder={field.type === 'select' && optIndex === 0 ? 'e.g. Choose one…' : 'Option text'}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="fb-btn-danger-sm"
                  onClick={() => {
                    const newOpts = (field.options || []).filter((_, i) => i !== optIndex);
                    updateField(field.id, { options: newOpts.length ? newOpts : ['Option 1'] });
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              className="fb-btn-ghost-sm"
              onClick={() => updateField(field.id, { options: [...(field.options || []), 'New option'] })}
            >
              Add option
            </button>
          </div>
        )}
        {field.type === 'number' && (
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
            <div style={{ flex: '1', minWidth: '120px' }}>
              <label className="fb-label" style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>Min value (optional)</label>
              <input
                type="number"
                className="fb-input"
                placeholder="e.g. 30"
                value={field.min !== undefined && field.min !== '' ? field.min : ''}
                onChange={(e) => updateField(field.id, { min: e.target.value === '' ? undefined : (parseFloat(e.target.value) || undefined) })}
              />
            </div>
            <div style={{ flex: '1', minWidth: '120px' }}>
              <label className="fb-label" style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>Max value (optional)</label>
              <input
                type="number"
                className="fb-input"
                placeholder="e.g. 1000"
                value={field.max !== undefined && field.max !== '' ? field.max : ''}
                onChange={(e) => updateField(field.id, { max: e.target.value === '' ? undefined : (parseFloat(e.target.value) || undefined) })}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function FieldsTab({
  isMobile,
  fields,
  rows,
  sensors,
  addField,
  addRow,
  removeRow,
  moveFieldToRow,
  updateRowTitle,
  updateField,
  deleteField,
}) {
  return (
        <div className={`fb-grid-fields${isMobile ? ' is-mobile' : ''}`}>
        {/* Field Palette */}
        <div className="fb-card fb-card-compact fb-card-fit">
          <h3 className="fb-subsection-title">Add Field</h3>
          <div className={`fb-palette${isMobile ? ' is-mobile' : ''}`}>
            {['text', 'email', 'textarea', 'select', 'radio', 'checkbox', 'number', 'phone', 'date'].map(type => (
              <button
                key={type}
                type="button"
                className={`fb-palette-btn${isMobile ? ' is-mobile' : ''}`}
                onClick={() => addField(type)}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Form Editor - row-based with drag-and-drop */}
        <div className="fb-card">
          <h3 className="fb-subsection-title">Form Fields</h3>
          
          {fields.length === 0 && rows.every(r => !(r.fieldIds || []).length) ? (
            <p className="fb-empty-state">
              No fields yet. Add a field from the sidebar to get started.
            </p>
          ) : (
            <DndContext
              sensors={sensors}
              onDragEnd={(event) => {
                const { active, over } = event;
                if (!over) return;
                const fieldId = active.id;
                const overId = over.id;
                const overRow = rows.find(r => r.id === overId);
                if (overRow) {
                  moveFieldToRow(fieldId, overId, undefined);
                  return;
                }
                const overRowContaining = rows.find(r => (r.fieldIds || []).includes(overId));
                if (overRowContaining) {
                  const ids = overRowContaining.fieldIds || [];
                  const overIndex = ids.indexOf(overId);
                  const draggedIndex = ids.indexOf(fieldId);
                  const insertIndex = draggedIndex >= 0 && draggedIndex < overIndex ? overIndex - 1 : overIndex;
                  moveFieldToRow(fieldId, overRowContaining.id, insertIndex);
                }
              }}
            >
              <div className="fb-rows-stack">
                {rows.map((row, rowIndex) => {
                  const fieldIds = row.fieldIds || [];
                  const rowFields = fieldIds.map(id => fields.find(f => f.id === id)).filter(Boolean);
                  const isEmpty = rowFields.length === 0;
                  return (
                    <DroppableRow
                      key={row.id}
                      rowId={row.id}
                      rowIndex={rowIndex}
                      rowTitle={row.title}
                      isEmpty={isEmpty}
                      onRemoveRow={() => removeRow(row.id)}
                      onTitleChange={(title) => updateRowTitle(row.id, title)}
                    >
                      {rowFields.map((field) => (
                        <DraggableFieldCard
                          key={field.id}
                          field={field}
                          updateField={updateField}
                          deleteField={deleteField}
                        />
                      ))}
                    </DroppableRow>
                  );
                })}
                <button
                  type="button"
                  className="fb-add-row-btn"
                  onClick={addRow}
                >
                  + Add row
                </button>
              </div>
            </DndContext>
          )}
        </div>
        </div>
  );
}
